import { I18N } from "oc-I18N";
import { Box3, Group, Material, Mesh, Object3D } from "three";

import { Jaco } from "oc-jaco3d/arrangement/Jaco";
import * as Scenes from "oc-threejs/Scenes";
import { memoizeUniversal } from "oc-commons/memoizeMultiMap";
import { AbstractPart3d } from "./AbstractPart3d";
import { v3 } from "oc-threejs/short3";
import { colorFromStyle, formatM, label3d } from "../commons/Utils";
import * as Textures from "oc-threejs/Textures";
import * as Commons from "oc-commons/Commons";
import * as Maths3 from "oc-threejs/Maths3";
import bauseitsIcon from "../style/icons/bauseits-icon.svg";
import { Images } from "oc-canvas2d/Images";
import { Kante, Polygon, PolygonKante } from "./schiebetor/types";
import { generateFrame } from "./schiebetor/frameGenerator";
import { extrudePolygons } from "./schiebetor/extrudePolygons";
import { ZElement } from "../arrangement/ZElement";
import { getMMR } from "../RichMetadata";
import { bevelSize, getSchiebetorMaterial } from "./schiebetor/erzeugeSchiebetorRahmen";

const unitCenterBox = new Box3().setFromCenterAndSize(v3(), v3(1, 1, 1));

export class Element3d extends AbstractPart3d {
  CLASS_NAME = "Element3d";
  schiebetorRahmen: Object3D;
  declare jaco: ZElement;
  selectLabels: Group;
  masshinweis: Group;

  constructor(jaco: Jaco) {
    super(jaco);
  }

  update3d() {
    super.update3d();
    this.updateSchiebetorRahmen();

    if (!APP.isTorkonfigurator) {
      this.showHeightAndWidthLabel();
      this.showMasshinweis();
    }
  }

  showHeightAndWidthLabel() {
    if (this.changed("selected") || this.changed("getBox") || this.changed("torMaxBreiteInfo")) {
      Scenes.remove(this.selectLabels);
      if (this.jaco.selected) {
        this.selectLabels = new Group();
        this.add(this.selectLabels);
        const box = this.jaco.getBox();
        // verschiebe selectLabels so, dass ihr Nullpunkt auf dem Boden liegt
        this.selectLabels.position.y = -this.jaco.localToWorld(v3(0, 0, 0)).y;
        const maxY = this.jaco.heightPhysical;
        const lichteWeite = this.jaco.lichteWeite;
        const xcenter = box.grid(0.5, 0, 0).x;
        const depth = 0.2;
        const lwbox = new Box3(
          v3(xcenter - lichteWeite / 2, 0, -depth / 2),
          v3(xcenter + lichteWeite / 2, maxY, +depth / 2)
        );
        const labelInfo = [];
        labelInfo.push([`⇤${formatM(lichteWeite)}⇥`, lwbox.grid(0.5, 0, 0).add(v3(0, 0.1, 0.2))]);
        if (APP.settings.userRole == "admin") {
          labelInfo.push([`⤒${formatM(lwbox.max.y)}`, lwbox.grid(0.5, 1, 0).add(v3(0, 0.1, 0))]);
        }
        const torMaxBreiteInfo = this.jaco.torMaxBreiteInfo;
        if (torMaxBreiteInfo) {
          labelInfo.push([
            I18N(torMaxBreiteInfo.hinweisI18NKey, I18N.formatFloat(torMaxBreiteInfo.breite)),
            lwbox.grid(0.5, 1, 0).add(v3(0, 0.3, 0)),
          ]);
        }
        labelInfo.forEach(([text, position]) => {
          const label = label3d(text);
          label.position.copy(position);
          this.selectLabels.add(label);
        });
        // const wireframe = wireframeBox(lwbox,APP.materials.create('materialSelectWireframe'));
        // this.selectLabels.add( wireframe );
      }
    }
  }

  async showMasshinweis() {
    if (
      !(
        this.changed("isStandardMass") ||
        this.changed("isBauseitskürzbaresElement") ||
        this.changed("lichteWeite") ||
        this.changed("hideTag")
      )
    ) {
      return;
    }

    Scenes.remove(this.masshinweis);
    this.masshinweis = null;

    if (!this.jaco.isStandardMass && !this.jaco.hideTag) {
      const color = "#" + colorFromStyle("--color-dark-2").getHexString();

      const masshinweis = (this.masshinweis = new Group());
      masshinweis.quaternion.copy(Maths3.quaternion.x270);
      this.add(masshinweis);

      const options = {
        supportMultiLine: true,
        cameraIndependendSize: false,
        color,
        orginalHeight: 0.15,
        backgroundColor: "rgba(255,255,255,0.01)",
      };

      const icon = this.jaco.isBauseitskürzbaresElement
        ? Textures.createImagePlane(await Images.loadImage(bauseitsIcon), {
            pixelPerUnit: 1500,
            backgroundColor: "rgba(255,255,255,0.01)",
          })
        : Textures.createTextPlane(I18N("fertigung_icon"), options);
      masshinweis.add(icon);

      const label = Textures.createTextSprite(
        this.jaco.isBauseitskürzbaresElement
          ? I18N("Bauseits auf Maß zugeschnitten.")
          : I18N("In Produktion auf Maß gefertigt."),
        { ...options, backgroundColor: "white" }
      );
      label.visible = false;
      label.position.set(0, -0.2, 0.5);
      masshinweis.add(label);

      (masshinweis as any).hoverable = true;
      (masshinweis as any).receivesMouseRaycasts = true;
      (masshinweis as any).onChangeInDragSelectHoverState = () => {
        label.visible = (masshinweis as any).hovering;
        STAGE.dirty();
      };

      this.masshinweis?.position.copy(
        this.jaco
          .getBox()
          .grid(0.5, 0, 0)
          .add(v3(0, 0, 0.4))
      );
    }
  }

  // @ts-ignore
  clone(recursive: boolean) {
    return new Element3d(this.jaco);
  }

  updateSchiebetorRahmen() {
    if (
      this.changed("isSchiebetor") ||
      this.changed("breite") ||
      this.changed("höhe") ||
      this.changed(
        "pfostenFarbe",
        (j) => (j as ZElement).pfostenLinks?.pfostenfarbeAuswahlwert?.Key
      ) ||
      this.changed(
        "schiebetormotorseiteAuswahlwert",
        (j) => (j as ZElement).schiebetormotorseiteAuswahlwert?.Key
      ) ||
      this.changed(
        "schieberichtungAuswahlwert",
        (j) => (j as ZElement).schieberichtungAuswahlwert?.Key
      )
    ) {
      Scenes.remove(this.schiebetorRahmen);
      this.schiebetorRahmen = null;
      const tor = this.jaco as ZElement;
      if (tor.isSchiebetor) {
        const polygonKanten = generateFrame(tor.schiebetorRahmenDef);
        type TiefeExtruder = {
          materialName?: string;
          tiefe?: number;
          extruder?: (
            kante: Kante,
            polygon: Polygon,
            materialName: string,
            materialMap: Map<string, string>
          ) => Mesh;
        };
        const cache = memoizeUniversal(
          (
            materialName?: string,
            tiefe?: number,
            extruder?: (
              kante: Kante,
              polygon: Polygon,
              materialName: string,
              materialMap: Map<string, string>
            ) => Mesh
          ) => ({
            materialName,
            tiefe,
            extruder,
          })
        );
        const extruderGruppen: Map<TiefeExtruder, PolygonKante[]> = Commons.groupBy(
          polygonKanten,
          (polygonKante: PolygonKante) => {
            const { materialName, tiefe, extruder }: TiefeExtruder = polygonKante.kante;
            return cache(materialName, tiefe, extruder);
          }
        );
        this.schiebetorRahmen = new Group();
        const mmr = getMMR(tor.rahmenMeta);
        extruderGruppen.forEach((polygonKanten, { materialName, tiefe, extruder }) => {
          if (extruder) {
            polygonKanten
              .map(({ kante, polygon }) => extruder(kante, polygon, materialName, mmr.MaterialMap))
              .forEach((mesh) => this.schiebetorRahmen.add(mesh));
          } else {
            const material = getSchiebetorMaterial(materialName, mmr.MaterialMap);
            const rahmenPolygons: Polygon[] = polygonKanten.map(({ polygon, kante }) => polygon);
            const rahmen = extrudePolygons(rahmenPolygons, material, tiefe, bevelSize);
            this.schiebetorRahmen.add(rahmen);
          }
        });
        this.jaco.schiebetorTranslationsPunkt.jaco3d.add(this.schiebetorRahmen);
      }
    }
  }
}
